import type { Sharks, TigerSharkState, World } from '../types';
import type { SiteId } from '../data/sites';
import { TIGER_SHARK, siteHasTigerSharks, stepTigerSharks } from '../sharks';
import { TIGER_SHARK_TABLE } from '../data/sharks/tiger';
import { WHITETIP_TABLE } from '../data/sharks/whitetip';
import { WHITETIP_SHARK } from '../whitetips';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';
import { wholeSeals } from './seals';

/** The tiger sharks' draws on their stock's stream of the day (`stockDraws`, one stock): binomial deaths on each class, a Poisson draw of the day's pups. */
export function sharkDraws(world: { seed: number; day: number }, siteId: SiteId, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.tigerShark, siteId, streamOf);
}

/** The whitetip reef sharks' draws (M219), as the tiger sharks' on their own stock's stream. */
export function whitetipDraws(world: { seed: number; day: number }, siteId: SiteId, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.whitetip, siteId, streamOf);
}

/**
 * Step 3 of the tick, the animals, after the deep slope (M197): each sanctuary's tiger sharks are born and die
 * (`sharks.ts`), then its whitetip reef sharks (M219), each on its own stream of the day (a test hands in its own as
 * `streamOf`).
 */
export function stepSharks(world: World, streamOf: typeof stockStream = stockStream): void {
  const epochDay = epochDayOf(world);
  for (const s of world.sanctuaries) {
    if (!s.sharks) continue;
    stepTigerSharks(TIGER_SHARK, s.sharks.tiger, epochDay, sharkDraws(world, s.siteId, streamOf));
    stepTigerSharks(WHITETIP_SHARK, s.sharks.whitetip, epochDay, whitetipDraws(world, s.siteId, streamOf));
  }
}

/** A sanctuary's whitetips as a world opens on a day (M219): the table's, stepped at expected values to the day, made whole, as the tiger sharks' (`openingSharks`). */
export function openingWhitetips(epochDay: number): TigerSharkState {
  const whitetip: TigerSharkState = { counts: [...WHITETIP_TABLE.counts] };
  for (let d = epochDayFromCivil(civilFromEpochDay(epochDay).year, 1, 1); d <= epochDay; d++) stepTigerSharks(WHITETIP_SHARK, whitetip, d, EXPECTED_DRAWS);
  return { counts: wholeSeals(whitetip.counts) };
}

/**
 * The tiger sharks (and the whitetips, `openingWhitetips`) as a world opens on a day: the table's (`data/sharks/`, a year's last day, its settled ages; the
 * sharks steady, so the same for every year), stepped at expected values through the opening year's days to the
 * opening day, the classes made whole sharks (`wholeSeals`; no draw: a world's opening draws nothing).
 */
export function openingSharks(epochDay: number): Sharks {
  const tiger: TigerSharkState = { counts: [...TIGER_SHARK_TABLE.counts] };
  for (let d = epochDayFromCivil(civilFromEpochDay(epochDay).year, 1, 1); d <= epochDay; d++) stepTigerSharks(TIGER_SHARK, tiger, d, EXPECTED_DRAWS);
  return { tiger: { counts: wholeSeals(tiger.counts) }, whitetip: openingWhitetips(epochDay) };
}

/** A sanctuary's sharks as a world opens on a day, where its site has them (`siteHasTigerSharks`); elsewhere none. */
export function sanctuaryOpeningSharks(siteId: SiteId, epochDay: number): Sharks | null {
  return siteHasTigerSharks(siteId) ? openingSharks(epochDay) : null;
}
